import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { FacebookIcon, TwitterIcon } from 'react-share';

import { Heading, LoadingSpinner, PageContainer, Section } from '../../../shared/ui';
import { appendYowanPreviewQueryIfNeeded } from '../../../app/host/yowanPreviewRouting';
import { fetchYowanPublicFounder } from '../app/yowanContentClient';
const { yowanPageContent } = require('./pageContent');
const { getYowanRouteMeta } = require('../routes/manifest');

const founderImageBackgroundStyle = {
  background: 'transparent',
};
const SOCIAL_ICON_SIZE = 28;

const getFounderSocialIcon = (label) => {
  if (label === 'Facebook') {
    return <FacebookIcon size={SOCIAL_ICON_SIZE} round />;
  }

  if (label === 'X' || label === 'Twitter') {
    return <TwitterIcon size={SOCIAL_ICON_SIZE} round />;
  }

  return null;
};

const getFounderImageSrc = (image = {}) => image.preview_url || image.thumbnail_url || image.url || '';

const FounderPage = () => {
  const content = yowanPageContent['/founder'];
  const metadata = getYowanRouteMeta('/founder');
  const location = useLocation();
  const [cassettes, setCassettes] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState('');

  useEffect(() => {
    if (!metadata) {
      return;
    }

    document.title = metadata.title;

    if (metadata.description) {
      const descriptionTag = document.querySelector('meta[name="description"]');
      if (descriptionTag) {
        descriptionTag.setAttribute('content', metadata.description);
      }
    }
  }, [metadata]);

  useEffect(() => {
    let isMounted = true;
    setIsLoading(true);
    setLoadError('');

    fetchYowanPublicFounder()
      .then((payload) => {
        if (isMounted) {
          setCassettes(payload.cassettes);
        }
      })
      .catch(() => {
        if (isMounted) {
          setCassettes([]);
          setLoadError('コンテンツを読み込めませんでした。');
        }
      })
      .finally(() => {
        if (isMounted) {
          setIsLoading(false);
        }
      });

    return () => {
      isMounted = false;
    };
  }, []);

  const primaryCassette = cassettes[0] || null;
  const secondaryCassette = cassettes[1] || null;
  const primaryImageSrc = getFounderImageSrc(primaryCassette?.primary_image);
  const secondaryImageSrc = getFounderImageSrc(secondaryCassette?.primary_image);

  return (
    <>
      <Section>
        <PageContainer>
          {primaryCassette ? (
            <div className="yowan-founder-profile">
              {primaryImageSrc ? (
                <div className="yowan-founder-profile__image" style={founderImageBackgroundStyle}>
                  <img
                    src={primaryImageSrc}
                    alt={primaryCassette.primary_image.alt_text || primaryCassette.title}
                    loading="lazy"
                    decoding="async"
                  />
                </div>
              ) : (
                <div
                  className="yowan-founder-profile__image"
                  style={founderImageBackgroundStyle}
                  aria-hidden="true"
                >
                  <span>{content.profile.portraitLabel}</span>
                </div>
              )}
              <div className="yowan-founder-profile__content c-surface-content">
                <p className="yowan-founder-profile__eyebrow">{content.profile.eyebrow}</p>
                <Heading as="h1">{primaryCassette.title}</Heading>
                <p className="yowan-founder-profile__bio">{primaryCassette.body}</p>
                <div className="yowan-founder-profile__links">
                  {content.profile.links.map((item) => (
                    item.external ? (
                      <a
                        key={item.label}
                        className="yowan-founder-profile__link yowan-social-icon-button"
                        href={item.href}
                        target="_blank"
                        rel="noreferrer"
                        aria-label={item.label}
                      >
                        {getFounderSocialIcon(item.label)}
                        <span className="u-visually-hidden">{item.label}</span>
                      </a>
                    ) : (
                      <Link
                        key={item.label}
                        className="yowan-founder-profile__link"
                        to={appendYowanPreviewQueryIfNeeded(item.href, location.search)}
                      >
                        {item.label}
                      </Link>
                    )
                  ))}
                </div>
              </div>
            </div>
          ) : (
            isLoading ? (
              <LoadingSpinner />
            ) : (
              <p className="yowan-admin-form__message">{loadError || '表示できるコンテンツがありません。'}</p>
            )
          )}
        </PageContainer>
      </Section>
      {secondaryCassette ? (
        <Section>
          <PageContainer>
            <div className="yowan-founder-touzen">
              <div className="yowan-founder-touzen__content c-surface-content">
                <Heading as="h2">{secondaryCassette.title}</Heading>
                <div className="yowan-founder-touzen__body u-stack-md">
                  <p>{secondaryCassette.body}</p>
                </div>
                <p className="yowan-founder-touzen__linkline">
                  刀禅についての詳しい情報は、
                  <a
                    className="yowan-founder-touzen__link"
                    href={content.touzen.officialLink.href}
                    target="_blank"
                    rel="noreferrer"
                  >
                    {content.touzen.officialLink.label}
                  </a>
                  を御覧ください。
                </p>
              </div>
              {secondaryImageSrc ? (
                <div className="yowan-founder-touzen__logo" style={founderImageBackgroundStyle}>
                  <img
                    src={secondaryImageSrc}
                    alt={secondaryCassette.primary_image.alt_text || secondaryCassette.title}
                    loading="lazy"
                    decoding="async"
                  />
                </div>
              ) : (
                <div
                  className="yowan-founder-touzen__logo"
                  style={founderImageBackgroundStyle}
                  aria-hidden="true"
                >
                  <span>{content.touzen.logoLabel}</span>
                </div>
              )}
            </div>
          </PageContainer>
        </Section>
      ) : null}
    </>
  );
};

export default FounderPage;
